import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { isEmpty } from "lodash";
import useLanguageChange from "../../hooks/useLanguageChange";
import { setIsAskAIDrawerOpen } from "../../reducers/common-reducer";
import { setUserQuestion, clearScreenshot, clearSmartActions, setCurrentPromptType, resetAskAIReducer } from "../../reducers/askAI-reducer";
import {
    handleQuickAssist,
    handleCaptureScreenshot,
    handleSendWithScreenshot,
    handleSendTextOnly,
    handleSmartActionClick,
} from "../../actions/askAIAction";
import { ASK_AI_PROMPT_TYPES } from "../../constants/AskAIConstants";
import { INPUT_VALIDATION_CONFIG } from "../../utils/validateUserInput";
import AskAIMessageBox from "./AskAIMessageBox";
import SmartActionButtons from "./SmartActionButtons";
import NewSnapshotBanner from "./NewSnapshotBanner";
import AskAIErrorBoundary from "./AskAIErrorBoundary";
import useCheckCallConnected from "../../hooks/useCheckCallConnected";
import { useTextareaRef } from "../../hooks/useTextareaRef";
import { createKeyboardHandler } from "../../utils/accessibilityHelpers";

const AskAIDrawer = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();

    const { isAskAIDrawerOpen } = useSelector((state) => state.commonReducer);
    const {
        messages,
        isAIResponding,
        capturedScreenshot,
        currentPromptType,
        userQuestion,
        smartActions,
        isGeneratingSmartActions,
        suggestNewSnapshot,
    } = useSelector((state) => state.askAIReducer);

    const isCallNotConnected = useCheckCallConnected();

    const {
        ref: inputRef,
        resetHeight,
        focus,
        autoResize,
    } = useTextareaRef({
        minHeight: 36,
        maxHeight: 110,
    });

    // Scroll container ref
    const scrollContainerRef = useRef(null);

    const handleCloseOnKeyDown = createKeyboardHandler(() => dispatch(setIsAskAIDrawerOpen(false)));

    const areControlsDisabled = isCallNotConnected;

    // Scroll to bottom of container
    const scrollToBottom = useCallback(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    }, []);

    // Scroll to the start of the last AI message
    // This MUST work even if user has scrolled up while waiting for response
    const scrollToLastAIMessage = useCallback(() => {
        const doScroll = () => {
            if (!scrollContainerRef.current) return;

            const container = scrollContainerRef.current;

            // Find all AI message elements
            const allAIMessages = container.querySelectorAll(".askai-message.ai-message");

            // Filter out the loading indicator (it doesn't have .askai-message-text)
            const actualAIMessages = Array.from(allAIMessages).filter((el) => el.querySelector(".askai-message-text"));

            if (actualAIMessages.length > 0) {
                const lastAIMessage = actualAIMessages[actualAIMessages.length - 1];

                // Get positions using getBoundingClientRect (viewport-relative)
                const containerRect = container.getBoundingClientRect();
                const elementRect = lastAIMessage.getBoundingClientRect();

                // Calculate: how far the element is from the top of the container's visible area
                // Then add current scrollTop to get absolute position
                const elementOffsetFromContainerTop = elementRect.top - containerRect.top;
                const scrollTarget = container.scrollTop + elementOffsetFromContainerTop - 10;

                // Force scroll
                container.scrollTop = scrollTarget;
            }
        };

        // Execute scroll multiple times to ensure it works
        doScroll();
        setTimeout(doScroll, 50);
        setTimeout(doScroll, 150);
    }, []);

    const handleCloseAskAIDrawer = () => {
        dispatch(setIsAskAIDrawerOpen(false));
    };

    const handleClearConversation = () => {
        dispatch(resetAskAIReducer());
        resetHeight();
    };

    const handleQuickAssistClick = () => {
        dispatch(handleQuickAssist());
    };

    const handleScreenshotAskClick = () => {
        dispatch(handleCaptureScreenshot());
    };

    const handleRetakeScreenshot = () => {
        dispatch(clearScreenshot());
        dispatch(handleCaptureScreenshot());
    };

    const handleOnInputChange = (e) => {
        const msgVal = e.target.value?.substring(0, INPUT_VALIDATION_CONFIG.MAX_MESSAGE_LENGTH);
        dispatch(setUserQuestion(msgVal));

        if (isEmpty(msgVal)) {
            resetHeight();
        } else {
            autoResize();
        }
    };

    const handleSendMessage = () => {
        const trimmedQuestion = userQuestion.trim();

        if (isEmpty(trimmedQuestion)) {
            return;
        }

        const currentScreenshot = capturedScreenshot;
        const currentType = currentPromptType;

        dispatch(setUserQuestion(""));
        dispatch(setCurrentPromptType(null));

        resetHeight();

        if (currentType === ASK_AI_PROMPT_TYPES.SCREENSHOT_ASK && currentScreenshot) {
            dispatch(handleSendWithScreenshot(currentScreenshot, trimmedQuestion));
        } else if (currentType === ASK_AI_PROMPT_TYPES.TEXT_ONLY) {
            dispatch(handleSendTextOnly(trimmedQuestion));
        }
    };

    const handleInitialTextSend = () => {
        const trimmedQuestion = userQuestion.trim();

        if (isEmpty(trimmedQuestion)) {
            return;
        }

        dispatch(handleSendTextOnly(trimmedQuestion));

        resetHeight();
    };

    const handleDeleteScreenshot = () => {
        dispatch(clearScreenshot());
        dispatch(setUserQuestion(""));
        dispatch(clearSmartActions());
        resetHeight();
    };

    const handleSmartActionClickHandler = (action) => {
        if (capturedScreenshot && action) {
            dispatch(handleSmartActionClick(capturedScreenshot, action));
        }
    };

    const handleOnKeyDown = (e) => {
        if (e && (e.key === "Enter" || e.which === 13) && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleInitialInputKeyDown = (e) => {
        if (e && (e.key === "Enter" || e.which === 13) && !e.shiftKey) {
            e.preventDefault();
            handleInitialTextSend();
        }
    };

    // ============================================================
    // SCROLL BEHAVIOR - Auto-scroll to new AI response
    // ============================================================
    // REQUIREMENT: When a NEW AI response arrives, ALWAYS scroll to the START
    // of that AI response bubble, regardless of user's current scroll position.
    //
    // Flow:
    // 1. User clicks Quick Assist / Snap & Ask / Smart Action / Capture
    // 2. USER message is added → scroll to bottom (shows user's action + loading)
    // 3. AI response arrives → scroll to START of AI response bubble
    // 4. SMART_ACTIONS arrives → no scroll (already visible below AI response)
    // ============================================================

    const prevMessageCountRef = useRef(0);

    useEffect(() => {
        const messageCount = messages.length;
        const prevCount = prevMessageCountRef.current;

        // Only react to NEW messages being added
        if (messageCount > 0 && messageCount > prevCount) {
            // Get all NEW messages that were added (handles batch updates)
            const newMessages = messages.slice(prevCount);

            // Check if any of the new messages is an AI message
            const hasNewAIMessage = newMessages.some((msg) => msg.type === "AI");
            const hasNewUserMessage = newMessages.some((msg) => msg.type === "USER");

            if (hasNewAIMessage) {
                // AI response arrived - scroll to the START of the AI message
                scrollToLastAIMessage();
            } else if (hasNewUserMessage) {
                // Only USER message (no AI yet) - scroll to bottom
                scrollToBottom();
            }
            // SMART_ACTIONS only: No scroll needed
        }

        prevMessageCountRef.current = messageCount;
    }, [messages, scrollToBottom, scrollToLastAIMessage]);

    useEffect(() => {
        if (capturedScreenshot || currentPromptType === ASK_AI_PROMPT_TYPES.TEXT_ONLY) {
            focus();
        }
    }, [capturedScreenshot, currentPromptType, focus]);

    const isInputDisabled =
        areControlsDisabled || isAIResponding || (currentPromptType === ASK_AI_PROMPT_TYPES.SCREENSHOT_ASK && !capturedScreenshot);
    const isSendDisabled = isInputDisabled || isEmpty(userQuestion.trim());

    const getPlaceholder = () => {
        if (currentPromptType === ASK_AI_PROMPT_TYPES.SCREENSHOT_ASK) {
            return localisedValues?.ask_ai_screenshot_placeholder;
        }
        return localisedValues?.ask_ai_input_placeholder;
    };

    const maxLength = INPUT_VALIDATION_CONFIG.MAX_MESSAGE_LENGTH;
    const remainingChars = maxLength - userQuestion.length;

    return (
        <div className={clsx("sidebar left-sidebar askai-sidebar", isAskAIDrawerOpen && "active")} id='askai-sidebar'>
            <div
                className='close-in-mobile'
                onClick={handleCloseAskAIDrawer}
                onKeyDown={handleCloseOnKeyDown}
                role='button'
                tabIndex={0}
                aria-label='Close Ask AI drawer'
            ></div>
            <div className='sidebar-in'>
                <div className='sidebar-head'>
                    <div className='sidebar-head-title'>
                        <div className='title'>{localisedValues?.ask_ai}</div>
                        <div className='custom-tooltip right-tooltip'>
                            <div className='custom-tooltip-icon'>
                                <span className='icon-info'></span>
                            </div>
                            <div className='custom-tooltip-content'>
                                <div className='custom-tooltip-in'>
                                    <p>{localisedValues?.ask_ai_info}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='sidebar-head-right'>
                        <div className='askai-clear-action custom-tooltip bottom-tooltip center-tooltip m-hide'>
                            <button
                                className='btn askai-clear-icon-btn'
                                onClick={handleClearConversation}
                                disabled={messages.length === 0 || isAIResponding}
                            >
                                <span className='for-icon icon-delete'></span>
                            </button>
                            <div className='custom-tooltip-content text-center'>
                                <div className='custom-tooltip-in'>
                                    <p>Clear Conversation</p>
                                </div>
                            </div>
                        </div>
                        <div className='sidebar-close'>
                            <button className='btn close-sidebar-button' onClick={handleCloseAskAIDrawer}>
                                <span className='for-icon icon-close-light'></span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className='sidebar-body askai-sidebar-body custom-scroll' ref={scrollContainerRef}>
                    <AskAIErrorBoundary>
                        {messages.length === 0 && !isAIResponding && (
                            <div className='askai-empty-state'>
                                <div className='askai-empty-state-content'>
                                    <h3 className='askai-empty-state-title'>Need help?</h3>
                                    <p className='askai-empty-state-text'>
                                        I can help you identify parts, diagnose issues, or generate troubleshooting steps.
                                    </p>
                                </div>
                            </div>
                        )}

                        {messages.map((message, index) => (
                            <AskAIMessageBox
                                message={message}
                                key={`askai_message_${index}_${message.timestamp}`}
                                onActionClick={handleSmartActionClickHandler}
                            />
                        ))}

                        {isAIResponding && (
                            <div className='askai-message ai-message'>
                                <div className='askai-message-content'>
                                    <div className='askai-typing-indicator'>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {messages.length > 0 && !currentPromptType && !isAIResponding && suggestNewSnapshot && (
                            <NewSnapshotBanner disabled={areControlsDisabled} />
                        )}
                    </AskAIErrorBoundary>
                </div>

                {!currentPromptType && !isAIResponding && (
                    <div className='sidebar-foot askai-prompts'>
                        <div className='askai-prompt-buttons'>
                            <div className='button'>
                                <div className='button-in custom-tooltip top-tooltip center-tooltip m-hide'>
                                    <button
                                        className='btn btn-primary askai-prompt-btn'
                                        onClick={handleQuickAssistClick}
                                        disabled={areControlsDisabled}
                                    >
                                        <span className='btn-text'>{localisedValues?.quick_assist}</span>
                                    </button>
                                    <div className='custom-tooltip-content text-center'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues?.quick_assist_tooltip}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='button'>
                                <div className='button-in custom-tooltip top-tooltip center-tooltip m-hide'>
                                    <button
                                        className='btn btn-primary askai-prompt-btn'
                                        onClick={handleScreenshotAskClick}
                                        disabled={areControlsDisabled}
                                    >
                                        <span className='btn-text'>{localisedValues?.screenshot_ask}</span>
                                    </button>
                                    <div className='custom-tooltip-content text-center'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues?.screenshot_ask_tooltip}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='askai-text-input-option'>
                            <div className='askai-option-divider'>
                                <span className='divider-line'></span>
                                <span className='divider-text'>{localisedValues?.or}</span>
                                <span className='divider-line'></span>
                            </div>
                            <div className='askai-message-form chat-message-form'>
                                <div className='swme-form-group swme-tabbar-form-group'>
                                    <div className='askai-inputchar-count'>{remainingChars}</div>
                                    <div className='swme-form-group-in'>
                                        <textarea
                                            ref={inputRef}
                                            className='swme-form-control custom-scroll-thin'
                                            placeholder={localisedValues?.ask_ai_input_placeholder}
                                            value={userQuestion}
                                            onChange={handleOnInputChange}
                                            onKeyDown={handleInitialInputKeyDown}
                                            disabled={areControlsDisabled || isAIResponding}
                                            autoComplete='off'
                                            required
                                            id='askai_input_initial'
                                            maxLength={maxLength}
                                        ></textarea>
                                        <button
                                            className='btn send-button'
                                            data-testid='askAISendInitialBtn'
                                            onClick={handleInitialTextSend}
                                            disabled={areControlsDisabled || isAIResponding || isEmpty(userQuestion.trim())}
                                        >
                                            <span className='btn-icon icon-send'></span>
                                        </button>
                                        <span className='for-bg'></span>
                                        <span className='for-border'></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentPromptType === ASK_AI_PROMPT_TYPES.SCREENSHOT_ASK && capturedScreenshot && (
                    <div className='sidebar-foot askai-input-section'>
                        <div className='askai-screenshot-preview'>
                            <img src={capturedScreenshot} alt='Screenshot preview' />
                            <div className='askai-preview-action'>
                                <div className='askai-preview-action-button custom-tooltip center-tooltip tooltip-auto-width'>
                                    <button
                                        className='btn btn-primary askai-preview-action-btn askai-retake-btn'
                                        onClick={handleDeleteScreenshot}
                                        disabled={areControlsDisabled || isAIResponding}
                                    >
                                        <span className='btn-icon icon-delete'></span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>Delete</p>
                                        </div>
                                    </div>
                                </div>
                                <div className='askai-preview-action-button custom-tooltip center-tooltip tooltip-auto-width'>
                                    <button
                                        className='btn btn-primary askai-preview-action-btn askai-retake-btn'
                                        onClick={handleRetakeScreenshot}
                                        disabled={areControlsDisabled || isAIResponding}
                                    >
                                        <span className='btn-icon icon-retake-camera'></span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>Retake</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <SmartActionButtons
                            actions={smartActions}
                            onActionClick={handleSmartActionClickHandler}
                            isLoading={isGeneratingSmartActions}
                            disabled={areControlsDisabled || isAIResponding}
                        />

                        <div className='askai-message-form chat-message-form'>
                            <div className='swme-form-group swme-tabbar-form-group'>
                                <div className='askai-inputchar-count'>{remainingChars}</div>
                                <div className='swme-form-group-in'>
                                    <textarea
                                        ref={inputRef}
                                        className='swme-form-control custom-scroll-thin'
                                        placeholder={getPlaceholder()}
                                        value={userQuestion}
                                        onChange={handleOnInputChange}
                                        onKeyDown={handleOnKeyDown}
                                        disabled={isInputDisabled}
                                        autoComplete='off'
                                        required
                                        id='askai_input'
                                        maxLength={maxLength}
                                    ></textarea>
                                    <button
                                        className='btn send-button'
                                        data-testid='askAISendIconBtn'
                                        onClick={handleSendMessage}
                                        disabled={isSendDisabled}
                                    >
                                        <span className='btn-icon icon-send'></span>
                                    </button>
                                    <span className='for-bg'></span>
                                    <span className='for-border'></span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentPromptType === ASK_AI_PROMPT_TYPES.TEXT_ONLY && (
                    <div className='sidebar-foot askai-input-section'>
                        <div className='askai-message-form chat-message-form'>
                            <div className='swme-form-group swme-tabbar-form-group'>
                                <div className='askai-inputchar-count'>{remainingChars}</div>
                                <div className='swme-form-group-in'>
                                    <textarea
                                        ref={inputRef}
                                        className='swme-form-control custom-scroll-thin'
                                        placeholder={getPlaceholder()}
                                        value={userQuestion}
                                        onChange={handleOnInputChange}
                                        onKeyDown={handleOnKeyDown}
                                        disabled={isInputDisabled}
                                        autoComplete='off'
                                        required
                                        id='askai_input'
                                        maxLength={maxLength}
                                    ></textarea>
                                    <button
                                        className='btn send-button'
                                        data-testid='askAISendIconBtn'
                                        onClick={handleSendMessage}
                                        disabled={isSendDisabled}
                                    >
                                        <span className='btn-icon icon-send'></span>
                                    </button>
                                    <span className='for-bg'></span>
                                    <span className='for-border'></span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AskAIDrawer;
